import React, { useCallback, useMemo, useState } from 'react';

import { graphql, PageProps } from 'gatsby';

import {
  Brochure,
  BrochuresRequestBody,
  ConversionFormData,
  CRM_RequestType,
  ServiceSegmentType,
} from '@mewa/types';

import { NavigationData } from '../../../components/header/types';
import { CantoImage } from '../../../components/strapi/canto-image';
import { setIsLocalEnv } from '../../../helpers/feature-dev';
import { LayoutType } from '../../../layouts/types';
import { sendFormToBackend } from '../../../send-forms/send-forms';
import { GlobalPageContext } from '../../../types';
import { Conversion } from '../components/conversion';
import { HashNavigation } from '../hash-navigation';
import {
  getFormSideImagesWithStrapiCantoImage,
  trackFormStep,
} from '../helper';
import { useFormState } from '../hooks/use-form-state';
import { parseFormHashWithInitialStep } from '../location-hash-parser';
import { ConversionTranslations } from '../translations-query';
import BrochuresSelection from './brochures-selection';
import {
  BrochureDownloads,
  BrochureInfo,
  CollectionImage,
  getBrochuresCheckboxes,
  getBrochuresHeaderCantoImage,
  getBrochuresNamesAndImages,
  getBrochuresURLs,
  ServiceSegments,
} from './helper';

export { Head } from '../../../layouts/head';

export const query = graphql`
  fragment DownloadItemFragment on STRAPI__COMPONENT_COMPONENTS_COLLECTION_BROCHURE {
    collectionUuid
    title
    file {
      fields {
        assetUrl
      }
      sizeInKiloBytes: size
      extension: ext
    }
  }
  query ($marketRegex: String!, $lang: String!, $locale: String!) {
    ...NavigationFragment
    ...ConversionTranslationsFragment
    ...BookConsultationFragment

    strapiBrochuresDownload(locale: { eq: $locale }) {
      title
      brandCatalog {
        fields {
          assetUrl
        }
        sizeInKiloBytes: size
        extension: ext
      }
      businessClothing {
        ...DownloadItemFragment
      }
      protectiveClothing {
        ...DownloadItemFragment
      }
      serviceClothing {
        ...DownloadItemFragment
      }
      workwear {
        ...DownloadItemFragment
      }
      healthClothing {
        ...DownloadItemFragment
      }
      hygieneClothing {
        ...DownloadItemFragment
      }
      washroomHygiene {
        ...DownloadItemFragment
      }
      oilCollectionMats {
        ...DownloadItemFragment
      }
      dirtTrappingMats {
        ...DownloadItemFragment
      }
      cleaningCloths {
        ...DownloadItemFragment
      }
      carCovers {
        ...DownloadItemFragment
      }
      wipingMops {
        ...DownloadItemFragment
      }
      partsCleaner {
        ...DownloadItemFragment
      }
      cleanRoom {
        ...DownloadItemFragment
      }

      sideImage {
        ...CantoImageFragment
      }

      headerImage {
        ...CantoImageFragment
      }

      brandCatalogImage {
        ...CantoImageFragment
      }
    }

    serviceSegments: allPimServiceSegment(filter: { lang: { eq: $lang } }) {
      nodes {
        name
        key
        assets {
          default
        }
      }
    }

    collectionImages: allPimCollection(filter: { lang: { eq: $lang } }) {
      nodes {
        uuid
        name
        assets {
          emotion
        }
      }
    }
  }
`;

type Props = NavigationData & {
  translations: ConversionTranslations;
  strapiBrochuresDownload: {
    title: string;
    sideImage: CantoImage;
    headerImage: CantoImage;
    brandCatalogImage: CantoImage;
  } & BrochureDownloads;
  serviceSegments: ServiceSegments;
  collectionImages: {
    nodes: CollectionImage[];
  };
  strapiBookConsultation: {
    thankYouImage: CantoImage;
  };
};

export type BrochuresLocationState = {
  preselectedBrochures?: ServiceSegmentType[];
};

const trackStepChange = (step: number) => {
  trackFormStep(step, 'brochure_download');
};

export const BrochuresDownload = ({
  data: {
    translations,
    strapiBrochuresDownload,
    serviceSegments,
    collectionImages,
    navigationLinks,
    navigationServiceSegments,
    strapiBookConsultation,
  },
  pageContext,
}: PageProps<Props, GlobalPageContext, BrochuresLocationState>) => {
  const [selectedBrochures, setSelectedBrochures] = useState<Brochure[]>([]);
  const formState = useFormState(CRM_RequestType.OrderService);

  setIsLocalEnv(pageContext.isLocal);

  const handleSelection = useCallback((brochures: Brochure[]) => {
    setSelectedBrochures(brochures);

    // go to step 1 of the form
    window.location.hash = '1';
  }, []);

  const { title, ...brochures } = useMemo(
    () => strapiBrochuresDownload,
    [strapiBrochuresDownload],
  );

  const availableCheckboxes: BrochureInfo[] = useMemo(
    () =>
      getBrochuresCheckboxes(
        brochures,
        getBrochuresNamesAndImages(
          translations,
          serviceSegments,
          strapiBrochuresDownload.brandCatalogImage,
          pageContext.market,
          pageContext.lang,
        ),
      ),
    [
      brochures,
      serviceSegments,
      strapiBrochuresDownload.brandCatalogImage,
      translations,
      pageContext,
    ],
  );

  const headerImages = useMemo(
    () => getBrochuresHeaderCantoImage(strapiBrochuresDownload.headerImage),
    [strapiBrochuresDownload.headerImage],
  );

  const formSideImages = getFormSideImagesWithStrapiCantoImage(
    strapiBrochuresDownload.sideImage,
    {
      desktop: { width: 1600, height: 1600 },
    },
  );

  const prepareFormData = (formData: ConversionFormData) => {
    const requestData: BrochuresRequestBody = {
      ...formData,
      requestType: CRM_RequestType.OrderService,
      orderedBrochures: selectedBrochures,
      language: pageContext.lang,
      market: pageContext.market,
    };

    return sendFormToBackend(requestData);
  };

  const maxAllowedStep =
    selectedBrochures.length > 0 ? formState.maxStep + 1 : 0;

  return (
    <HashNavigation
      stepForHash={parseFormHashWithInitialStep}
      maxAllowedStep={maxAllowedStep}
      state={formState}
      renderStep={(step, formState) => {
        return step === 0 ? (
          <BrochuresSelection
            mobileStageImage={headerImages.mobile}
            stageImage={headerImages.desktop}
            onSelect={handleSelection}
            translations={translations}
            availableBrochures={availableCheckboxes}
            navigationLinks={navigationLinks}
            langPrefix={pageContext.langPrefix}
          />
        ) : (
          <Conversion
            formState={formState}
            formType={CRM_RequestType.OrderService}
            selectedStep={step - 1}
            market={pageContext.market}
            lang={pageContext.lang}
            langPrefix={pageContext.langPrefix}
            onSubmit={prepareFormData}
            translations={translations}
            imageSrc={formSideImages.desktop}
            mobileImageSrc={formSideImages.mobile}
            formTitle={title}
            brochures={getBrochuresURLs(
              selectedBrochures,
              getBrochuresNamesAndImages(
                translations,
                serviceSegments,
                strapiBrochuresDownload.brandCatalogImage,
                pageContext.market,
                pageContext.lang,
              ),
              strapiBrochuresDownload,
              collectionImages.nodes,
            )}
            navigationLinks={navigationLinks}
            trackingEvent="form_brochure_download_submit"
            serviceSegmentLinks={navigationServiceSegments.nodes}
            thankYouImage={strapiBookConsultation.thankYouImage}
            featureDev={pageContext.featureDev}
          ></Conversion>
        );
      }}
      onStepChange={trackStepChange}
    />
  );
};

BrochuresDownload.layout = LayoutType.Minimal;

export default BrochuresDownload;
